/**
 * YoutubeVideo — Lightweight facade for YouTube embeds.
 *
 * Loading strategy (addresses Jira perf ticket):
 * 1. On initial render: shows a static thumbnail + play button (no iframe, no YouTube scripts).
 * 2. On user interaction (click or Enter/Space): the facade is replaced with the real iframe
 *    and the YouTube IFrame API script is injected dynamically — deferred until this point so
 *    it has zero impact on page load, TBT, and INP.
 * 3. The YT script tag is injected via requestIdleCallback (with a setTimeout fallback) so it
 *    doesn't block the main thread immediately after the click.
 * 4. The iframe carries loading="lazy" as an additional hint to browsers that support it.
 *
 * Third-parties deferred by this component:
 * - https://www.youtube.com/iframe_api  — injected only after user clicks play
 * - youtube.com embed iframe            — src not set until activation (no network request before click)
 */
import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TrackingClient } from '@sparefoot/tracking-client';
import { Image } from 'components/core/Image';
import youtubeSvg from 'assets/images/yt-thumbnails/youtube-play.svg';
import loaderSvg from 'assets/images/yt-thumbnails/Loader.svg';
import infoSvg from 'assets/images/yt-thumbnails/info.svg';
import GTM_CLASSNAMES from 'utils/gtmSelectors';

import './YoutubeVideo.scss';

const THUMBNAIL_QUALITY = {
	maxres: 'maxresdefault.webp',
	sd: 'sddefault.webp',
	hq: 'hqdefault.webp',
	mq: 'mqdefault.webp',
};

const getYoutubeThumbnail = (videoId, quality = 'maxres') =>
	`https://i.ytimg.com/vi_webp/${videoId}/${THUMBNAIL_QUALITY[quality] ?? THUMBNAIL_QUALITY.maxres}`;

const YOUTUBE_EMBED_PREFIX = 'https://www.youtube.com/embed/';
// YouTube video IDs are always exactly 11 URL-safe base64 characters
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

const extractVideoId = (src) => {
	try {
		const normalized = src.startsWith('//') ? `https:${src}` : src;
		if (!normalized.startsWith(YOUTUBE_EMBED_PREFIX)) {
			console.error('[YoutubeVideo] Malformed youtube link:', src);
			return null;
		}
		const url = new URL(normalized);
		const id = url.pathname.split('/embed/')[1]?.split('?')[0] || null;
		if (!id || !YOUTUBE_ID_RE.test(id)) {
			console.error('[YoutubeVideo] Invalid video ID:', id);
			return null;
		}
		return id;
	} catch {
		console.error('[YoutubeVideo] Malformed youtube link:', src);
		return null;
	}
};

// Appends enablejsapi + autoplay so the video starts immediately after the
// facade is replaced. autoplay requires a prior user gesture — clicking the
// facade satisfies that requirement.
const buildSrc = (src) => {
	try {
		const url = new URL(src.startsWith('//') ? `https:${src}` : src);
		url.searchParams.set('enablejsapi', '1');
		url.searchParams.set('autoplay', '1');
		return url.toString();
	} catch {
		return src;
	}
};

/**
 * Injects the YouTube IFrame API script via requestIdleCallback so it does
 * not block the main thread immediately after the user's click. Falls back to
 * setTimeout(0) for browsers that don't support requestIdleCallback (Safari <18).
 */
const loadYouTubeScript = (onReady) => {
	if (globalThis.YT && globalThis.YT.Player) {
		onReady();
		return;
	}

	const inject = () => {
		// YT may have loaded during the idle/setTimeout delay — re-check before injecting.
		if (globalThis.YT?.Player) {
			onReady();
			return;
		}

		// If another instance already queued the script, just chain onto the
		// existing global callback instead of appending a duplicate script tag.
		if (
			document.querySelector(
				'script[src="https://www.youtube.com/iframe_api"]',
			)
		) {
			const prev = globalThis.onYouTubeIframeAPIReady;
			globalThis.onYouTubeIframeAPIReady = () => {
				prev?.();
				onReady();
			};
			return;
		}

		globalThis.onYouTubeIframeAPIReady = onReady;
		const tag = document.createElement('script');
		// async so the parser is not blocked while the script downloads
		tag.async = true;
		tag.src = 'https://www.youtube.com/iframe_api';
		document.body.appendChild(tag);
	};

	// Defer script injection to an idle period to keep main-thread free
	if (typeof globalThis.requestIdleCallback === 'function') {
		globalThis.requestIdleCallback(inject, { timeout: 2000 });
	} else {
		setTimeout(inject, 0);
	}
};

// ---------------------------------------------------------------------------
// Shared error UI — shown when video ID is invalid or video is unavailable
// Mirrors YouTube's own "Video unavailable" treatment: dark background + message
// ---------------------------------------------------------------------------
const VideoUnavailable = ({ videoName }) => (
	<div
		className="ph-responsive-embed yt-unavailable"
		role="alert"
		aria-label={`Video unavailable: ${videoName}`}
	>
		<div className="yt-unavailable__inner">
			<Image
				className="yt-unavailable__icon"
				src={infoSvg}
				alt=""
				aria-hidden="true"
			/>
			<p className="yt-unavailable__title">Video unavailable</p>
			<p className="yt-unavailable__subtitle">
				This video is no longer available
			</p>
		</div>
	</div>
);

VideoUnavailable.propTypes = {
	videoName: PropTypes.string.isRequired,
};

// ---------------------------------------------------------------------------
// Inner player — only mounted after the user activates the facade
// ---------------------------------------------------------------------------
const YoutubeVideoPlayer = ({ src, videoName }) => {
	const iframeRef = useRef(null);
	const reactId = useId();
	const playerIdRef = useRef(`youtube-player-${reactId.replace(/:/g, '')}`);
	const [videoDuration, setVideoDuration] = useState(0);
	const [playedTime, setPlayedTime] = useState(0);
	const [hasSentEvent, setHasSentEvent] = useState(false);
	const [player, setPlayer] = useState(null);
	const [intervalId, setIntervalId] = useState(null);
	// videoError: true when YT fires onError (video removed, private, or invalid)
	const [videoError, setVideoError] = useState(false);
	// isLoading: true from activation until the video starts playing for the first time
	const [isLoading, setIsLoading] = useState(true);

	const hasSentEventRef = useRef(hasSentEvent);
	const playerRef = useRef(player);
	const intervalRef = useRef(intervalId);
	const playedTimeRef = useRef(playedTime);
	const videoDurationRef = useRef(videoDuration);

	hasSentEventRef.current = hasSentEvent;
	playerRef.current = player;
	intervalRef.current = intervalId;
	playedTimeRef.current = playedTime;
	videoDurationRef.current = videoDuration;

	const trackVideoEvent = useCallback(() => {
		const payload = {
			video_duration: videoDurationRef.current,
			video_duration_played: playedTimeRef.current,
			video_name: videoName,
		};

		setHasSentEvent(true);
		try {
			TrackingClient.trackVideoWatched(payload);
		} catch (err) {
			console.error('[YoutubeVideo] trackVideoWatched failed:', err);
		}
	}, [videoName]);

	const onStateChange = useCallback(
		(event) => {
			// Called on every player state change: playing, paused, ended, buffering, unstarted
			const playerState = globalThis.YT.PlayerState;
			if (event.data === playerState.PLAYING && !intervalRef.current) {
				setIsLoading(false);
				const newInterval = setInterval(() => {
					if (playerRef.current) {
						setPlayedTime(playerRef.current.getCurrentTime());
					}
				}, 1000);
				setIntervalId(newInterval);
			} else if (playedTimeRef.current > 10 && !hasSentEventRef.current) {
				// Video played >10 s and tracking event not yet sent — fire it now
				trackVideoEvent();
				clearInterval(intervalRef.current);
				setIntervalId(null);
			} else {
				clearInterval(intervalRef.current);
				setIntervalId(null);
			}
		},
		[trackVideoEvent],
	);

	const onYouTubeIframeAPIReady = useCallback(() => {
		// If the iframe doesn't exist, or the YT global isn't ready return
		if (!iframeRef.current || !globalThis.YT) return;

		// Bind the iframe element to the YT Player and attach event handlers
		iframeRef.current.id = playerIdRef.current;
		const newPlayer = new globalThis.YT.Player(iframeRef.current.id, {
			events: {
				onReady: (event) => {
					setVideoDuration(event.target.getDuration());
					// Clear spinner here so autoplay-blocked videos don't stay
					// stuck — PLAYING may never fire if the browser suppresses
					// unmuted autoplay.
					setIsLoading(false);
				},
				onStateChange,
				// YT error codes: 2 = invalid param, 5 = HTML5 error,
				// 100 = video not found/removed, 101/150 = embed not allowed
				onError: () => setVideoError(true),
			},
		});
		setPlayer(newPlayer);
	}, [onStateChange]);

	// Dynamically inject the YouTube IFrame API script after user interaction.
	// The script is deferred via requestIdleCallback — see loadYouTubeScript above.
	useEffect(() => {
		loadYouTubeScript(onYouTubeIframeAPIReady);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Clean up the polling interval when the component unmounts (e.g. navigation)
	useEffect(() => {
		return () => {
			clearInterval(intervalRef.current);
		};
	}, []);

	if (videoError) {
		return <VideoUnavailable videoName={videoName} />;
	}

	return (
		<div
			className={classNames(
				'ph-responsive-embed',
				GTM_CLASSNAMES.YOUTUBE_VIDEO_WRAPPER,
			)}
		>
			{isLoading && (
				<div className="yt-player__loader">
					<Image
						className="yt-player__loader-icon"
						src={loaderSvg}
						alt=""
						aria-hidden="true"
					/>
				</div>
			)}
			{/* loading="lazy" hints to supporting browsers to defer sub-resource loading */}
			<iframe
				title={videoName || 'video'}
				src={buildSrc(src)}
				ref={iframeRef}
				loading="lazy"
				allowFullScreen
				allow="autoplay; encrypted-media"
				referrerPolicy="strict-origin-when-cross-origin"
			/>
		</div>
	);
};

YoutubeVideoPlayer.propTypes = {
	src: PropTypes.string.isRequired,
	videoName: PropTypes.string.isRequired,
};

// Renders a full-coverage anchor inside a <noscript> tag so it only applies
// when JS is disabled. The anchor sits as an absolute overlay over the entire
// facade (thumbnail + play icon), making the whole area clickable without JS.
// The tree is identical on server and client, so there is no hydration mismatch.
const NoScriptYoutubeVideoFallback = ({ videoId }) => (
	<noscript>
		<a
			href={`https://www.youtube.com/watch?v=${videoId}`}
			target="_blank"
			rel="noreferrer noopener"
			className="yt-facade__noscript-link"
			aria-label="Watch on YouTube"
		/>
	</noscript>
);

NoScriptYoutubeVideoFallback.propTypes = {
	videoId: PropTypes.string.isRequired,
};

// ---------------------------------------------------------------------------
// Facade — rendered on initial page load; replaced by the player on activation
// ---------------------------------------------------------------------------
const YoutubeVideo = ({
	src,
	videoName,
	lazyLoadThumbnail = true,
	thumbnailQuality = 'maxres',
}) => {
	const [activated, setActivated] = useState(false);
	const videoId = extractVideoId(src);
	const [thumbnailSrc, setThumbnailSrc] = useState(
		videoId ? getYoutubeThumbnail(videoId, thumbnailQuality) : null,
	);

	// No valid video ID means the src is malformed — nothing to play
	if (!videoId) {
		return <VideoUnavailable videoName={videoName} />;
	}

	// maxresdefault only exists for 720p+ uploads — fall back to hqdefault on 404
	const handleThumbnailError = () => {
		const fallback = getYoutubeThumbnail(videoId, 'hq');
		if (thumbnailSrc !== fallback) setThumbnailSrc(fallback);
	};

	const activate = () => setActivated(true);

	const handleKeyDown = (e) => {
		// Support both Enter and Space, matching native button behaviour
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			activate();
		}
	};

	if (activated) {
		return (
			<YoutubeVideoPlayer
				src={src}
				videoName={videoName}
			/>
		);
	}

	return (
		<div
			className={classNames(
				'ph-responsive-embed',
				'yt-facade',
				GTM_CLASSNAMES.YOUTUBE_VIDEO_WRAPPER,
			)}
			role="button"
			tabIndex={0}
			aria-label={`Play video: ${videoName}`}
			onClick={activate}
			onKeyDown={handleKeyDown}
		>
			<NoScriptYoutubeVideoFallback videoId={videoId} />
			{thumbnailSrc && (
				<Image
					className="yt-facade__thumbnail"
					src={thumbnailSrc}
					alt=""
					loading={lazyLoadThumbnail ? 'lazy' : 'eager'}
					onError={handleThumbnailError}
				/>
			)}
			<Image
				className="yt-facade__play-icon"
				src={youtubeSvg}
				alt=""
				aria-hidden="true"
			/>
		</div>
	);
};

YoutubeVideo.propTypes = {
	src: PropTypes.string.isRequired,
	videoName: PropTypes.string.isRequired,
	lazyLoadThumbnail: PropTypes.bool,
	thumbnailQuality: PropTypes.oneOf(['maxres', 'sd', 'hq', 'mq']),
};

export default YoutubeVideo;
